import { SelectOption } from '@globaleris/ui.library'
import moment from 'moment'
import {
  EnumCommodity,
  EnumEstadoCliente,
  EnumEstadoProductoCapacidad,
  EnumSubtipoSujeto,
  EnumTipoPeaje,
  EnumTipoSujeto
} from '../../api'
import { apiGetAnnexedContracts } from '../../api/Argentia/endpoints/annexedParams/annexedParams.api'
import { AnnexedParamsModel } from '../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import {
  apiGetCapacitiesInBetweenRange,
  apiPatchCapacity
} from '../../api/Argentia/endpoints/capacities/capacities.api'
import { apiGetAccountsForSubject } from '../../api/Argentia/endpoints/clients/clients.api'
import {
  apiChangeOwner,
  apiCheckContractSignDateByProcedureId,
  apiCreateContractQuantityForContract,
  apiDeleteAnnexedContract,
  apiDeleteContractOrders,
  apiFetchATRTypes,
  apiFetchActiveContractByCups,
  apiFetchActivityTypes,
  apiFetchBankingEntity,
  apiFetchBillingCycleBySubjectType,
  apiFetchContractByID,
  apiFetchContractTypes,
  apiFetchContractsByCups,
  apiFetchContractsBySubjectID,
  apiFetchExistingCUPS,
  apiFetchHoldOptions,
  apiFetchPaymentMethodOptions,
  apiFetchPaymentMethods,
  apiFetchPowerPeriodsByToll,
  apiFetchPressureGroup,
  apiFetchProductsByContractID,
  apiFetchProductsBySettlementID,
  apiFetchProductsBySubjectSubType,
  apiFetchServices,
  apiFetchTolls,
  apiGetCapacitiesOrATRForContract,
  apiGetContractByCode,
  apiGetContractOrders,
  apiGetEntityBankByIBAN,
  apiGetExistingCUPSByCode,
  apiPatchContract,
  apiPatchContractOrders,
  apiPostAnnexedContract,
  apiPostContractOrders,
  apiPutAnnexedContract,
  apiRevertChangeOwner,
  apiSaveContract,
  apiTerminateContract,
  apiUpdateContract,
  apiUpdateContractQuantityForContract,
  apiUpdateContractSupplies
} from '../../api/Argentia/endpoints/contracts/contracts.api'
import {
  AnnexedContractModelPatchRequest,
  AnnexedContractModelPostRequest,
  ContractOrdersModel,
  ContractOrdersRequestModel,
  ContractQuantity,
  ContractQuantityRequest,
  NewContractQuantity,
  ProductSelectOption
} from '../../api/Argentia/endpoints/contracts/model/contracts'
import { apiFetchSettlementGroupsByContractID } from '../../api/Argentia/endpoints/settlementGroups/settlementGroups.api'
import {
  ActiveContractModelResponse,
  CUPSModel,
  ContractModel,
  PaymentMethodTypeModel
} from '../../api/Argentia/model/contracts'
import { Account } from '../../api/Argentia/model/subjects'
import { ApiTrades } from '../../api/Pioneer/endpoints/trades/trades.api'
import { ContractProductAPIModel } from '../../api/Pioneer/model/Contracts'
import {
  createErrorServiceResponse,
  createSuccessServiceResponse
} from '../../helpers/serviceResponseHelper'
import { ServiceResponse } from '../../model/serviceResponse'
import { ActionIcons, DataTableActionConfiguration } from '../../model'
import i18n from '../../i18n'
import { generateIconsFromConfiguration } from '../../helpers/dataTableActionIconHelper'
import { notifyError } from '../../redux'
import { SubjectModel } from '../clients/clients.model'
import { ChangeOwnerForm } from './changeOwner/contracts.changeOwner'
import {
  changeOwnerNewContractMapper,
  newContractMapper,
  parseDate,
  updateContractBatchMapper
} from './contracts.mapper'
import {
  ChangeContractOwnershipModel,
  ChangeOwnerShipATRFormModel,
  ContractCapacitiesFormModel,
  ContractDataFormModel,
  ContractForm,
  ContractFormRaw,
  NewAddressModel,
  PatchContractSuppliesBatchRequest,
  RevertChangeOwnershipModel,
  TerminateContractOwnershipModel
} from './contracts.model'
import { CHANGE_TYPE } from './changeOwner/components/cupsFilter'
import { EnumPeriodo } from '../../api/Argentia/enums/periodo'
import { enumEnumToValueNumber } from '../../helpers'

const apiTrades = new ApiTrades()

export const fetchProductsBySubjectSubType = async (
  clientSubtype: EnumSubtipoSujeto,
  commodity: EnumCommodity
): Promise<ServiceResponse<ProductSelectOption[] | undefined>> => {
  return await apiFetchProductsBySubjectSubType(clientSubtype, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchServices = async (clientType: EnumTipoSujeto): Promise<ServiceResponse<any>> => {
  return await apiFetchServices(clientType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchContractTypes = async (
  subjectType: EnumTipoSujeto,
  commodity?: EnumCommodity | null
): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchContractTypes(subjectType, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchTolls = async (commodity: EnumCommodity): Promise<ServiceResponse<any>> => {
  return await apiFetchTolls(commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPressureGroup = async (
  commodity: EnumCommodity
): Promise<ServiceResponse<any>> => {
  return await apiFetchPressureGroup(commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchBillingCycleBySubjectType = async (
  clientType: EnumTipoSujeto
): Promise<ServiceResponse<any>> => {
  return await apiFetchBillingCycleBySubjectType(clientType)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPaymentMethods = async (): Promise<
  ServiceResponse<SelectOption[] | undefined>
> => {
  return await apiFetchPaymentMethodOptions()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchHoldOptions = async (): Promise<ServiceResponse<SelectOption[] | undefined>> => {
  return await apiFetchHoldOptions()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPaymentMethodsTypes = async (): Promise<
  ServiceResponse<PaymentMethodTypeModel[] | undefined>
> => {
  return await apiFetchPaymentMethods()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchATRTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchATRTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchActivityTypes = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchActivityTypes()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchBankingEntity = async (): Promise<ServiceResponse<any>> => {
  return await apiFetchBankingEntity()
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchExistingCUPS = async (
  codeCUPS: string,
  commodity: EnumCommodity
): Promise<ServiceResponse<CUPSModel | undefined>> => {
  return await apiFetchExistingCUPS(codeCUPS, commodity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      notifyError('api error')
      return createErrorServiceResponse(error)
    })
}

export const fetchCUPSByCode = async (
  codeCUPS: string,
  commodity: EnumCommodity
): Promise<ServiceResponse<CUPSModel | undefined>> => {
  return await apiFetchExistingCUPS(codeCUPS, commodity)
    .then(response => {
      return createSuccessServiceResponse(response.id ? response : undefined)
    })
    .catch(error => {
      notifyError('api error')
      return createErrorServiceResponse(error)
    })
}

export const fetchContractsByCups = async (
  codeCUPS: string,
  companyId: number,
  iniDate: Date,
  endDate?: Date
): Promise<ServiceResponse<any>> => {
  return await apiFetchContractsByCups(codeCUPS, companyId, parseDate(iniDate), parseDate(endDate))
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const saveContract = async (
  contractDataForm: ContractDataFormModel,
  clientData: SubjectModel,
  commodity: EnumCommodity,
  switchingRole: boolean
): Promise<ServiceResponse<any>> => {
  const address: NewAddressModel = {
    countryId: contractDataForm.countryId ?? clientData.fiscalAddress.countryId!,
    provinceId: contractDataForm.provinceId ?? clientData.fiscalAddress.provinceId!,
    municipalityId: contractDataForm.municipalityId ?? clientData.fiscalAddress.municipalityId!,
    postalAddress: contractDataForm.postalAddress ?? clientData.fiscalAddress.postalAddress,
    postalCode: contractDataForm.postalCodeSearch?.value
      ? `${contractDataForm.postalCodeSearch?.value}`
      : clientData.fiscalAddress.postalCode!,
    country: contractDataForm.country ?? clientData.fiscalAddress.country,
    province: contractDataForm.province ?? clientData.fiscalAddress.province,
    municipality: contractDataForm.municipality ?? clientData.fiscalAddress.municipality
  }

  return await apiSaveContract(
    newContractMapper(
      { ...contractDataForm, subjectID: clientData!.id! },
      address,
      commodity,
      switchingRole,
      clientData?.type
    )
  )
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchPowerPeriodsByToll = async (
  toll: EnumTipoPeaje
): Promise<ServiceResponse<any>> => {
  return await apiFetchPowerPeriodsByToll(toll)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchContractByID = async (contractID: number): Promise<ServiceResponse<any>> => {
  return await apiFetchContractByID(contractID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchContractsBySubjectID = async (
  subjectID: number
): Promise<ServiceResponse<ContractModel[] | undefined>> => {
  return await apiFetchContractsBySubjectID(subjectID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductsByContractID = async (
  contractID: number
): Promise<ServiceResponse<ContractProductAPIModel[] | undefined>> => {
  return await apiFetchProductsByContractID(contractID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchProductsBySettlementGroupID = async (
  settlementGroupId: number
): Promise<ServiceResponse<ContractProductAPIModel[] | undefined>> => {
  return await apiFetchProductsBySettlementID(settlementGroupId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchBooks = async (): Promise<SelectOption[]> => {
  return await apiTrades.FetchBooks()
}

export const patchContract = async (request: ContractModel) => {
  return await apiPatchContract(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateContract = async (
  contractID: number,
  contractDataForm: ContractFormRaw,
  oldContractData: ContractForm
): Promise<ServiceResponse<any>> => {
  const contractData: ContractForm = {
    ...contractDataForm,
    postalCode: `${contractDataForm.postalCodeSearch.textValue}`
  }

  const request = updateContractBatchMapper(contractID, contractData, oldContractData)

  if (
    !request.contract &&
    !request.account &&
    !request.address &&
    !request.paymentMethod &&
    !request.products &&
    !request.deletedProducts
  ) {
    return { success: true }
  }

  return await apiUpdateContract(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateContractSupplies = async (request: PatchContractSuppliesBatchRequest) => {
  if (
    !request.cups &&
    !request.address &&
    !request.contractATR &&
    !request.contractATRDetails &&
    !request.powers &&
    !request.capacities
  ) {
    return { success: true }
  }

  return await apiUpdateContractSupplies(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateContractCapacities = async (
  newCapacity: ContractCapacitiesFormModel,
  capacityId: number
) => {
  return await apiPatchCapacity(newCapacity, capacityId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchCapacitiesInBetweenRange = async (
  contractID: number,
  newStartDate: string,
  newEndDate: string
) => {
  return await apiGetCapacitiesInBetweenRange(contractID, newStartDate, newEndDate)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getContractDetails = async (
  contractID: number
): Promise<ServiceResponse<ContractModel | undefined>> => {
  return await apiGetCapacitiesOrATRForContract(contractID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getExistingCUPSByCode = async (
  codeCUPS: string
): Promise<ServiceResponse<CUPSModel | undefined>> => {
  return await apiGetExistingCUPSByCode(codeCUPS)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getActiveContractByCups = async (
  codeCUPS: string
): Promise<ServiceResponse<ActiveContractModelResponse | null | undefined>> => {
  return await apiFetchActiveContractByCups(codeCUPS, 1)
    .then(response => {
      if (response.id) {
        return createSuccessServiceResponse(response)
      }
      return createSuccessServiceResponse(null)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkDuplicateContractCode = async (
  code: string,
  id?: number
): Promise<ServiceResponse<{ isValid: boolean } | undefined>> => {
  return await apiGetContractByCode(code)
    .then(contracts => {
      let exists = false
      if (contracts && contracts.length > 0) {
        exists = id
          ? contracts.some(contract => contract.id !== id && contract.contractCode === code)
          : contracts.some(contract => contract.contractCode === code)
      }
      return createSuccessServiceResponse({ isValid: !exists })
    })
    .catch(error => createErrorServiceResponse(error))
}

export const checkValidIBAN = async (
  IBAN: string
): Promise<ServiceResponse<{ isValid: boolean } | undefined>> => {
  return await apiGetEntityBankByIBAN(IBAN)
    .then(response => {
      if (response && response.length === 0) {
        // In case Entity Bank is not found, IBAN is NOT valid.
        return createSuccessServiceResponse({ isValid: false })
      }
      return createSuccessServiceResponse({ isValid: true })
    })
    .catch(error => {
      if (error.statusCode === 404) {
        return createSuccessServiceResponse({ isValid: false })
      }
      return createErrorServiceResponse(error)
    })
}

export const changeOwner = async (
  dataForm: ChangeOwnerForm,
  activeContract: ActiveContractModelResponse,
  clientID: number
): Promise<ServiceResponse<any>> => {
  const commodity = enumEnumToValueNumber(EnumCommodity, activeContract.commodity)

  const contract = changeOwnerNewContractMapper(dataForm, activeContract)

  const atr: ChangeOwnerShipATRFormModel = {
    ...(commodity === EnumCommodity.GasNatural &&
      dataForm.toll && {
        tipoProductoCapacidad: dataForm.type,
        tollType: dataForm.toll,
        qdContratada: dataForm.qdContratada,
        qaEstimada: dataForm.qaEstimada,
        contractType: dataForm.contractType,
        state: EnumEstadoProductoCapacidad.Borrador
      }),
    ...(commodity === EnumCommodity.Electricidad &&
      dataForm.tollType && {
        contractAtrType: dataForm.type,
        tollType: dataForm.tollType,
        contractType: dataForm.contractType,
        state: EnumEstadoProductoCapacidad.Borrador,
        powers: [
          {
            period: EnumPeriodo.Periodo1,
            value: dataForm.period1!
          },
          {
            period: EnumPeriodo.Periodo2,
            value: dataForm.period2!
          },
          {
            period: EnumPeriodo.Periodo3,
            value: dataForm.period3!
          },
          {
            period: EnumPeriodo.Periodo4,
            value: dataForm.period4!
          },
          {
            period: EnumPeriodo.Periodo5,
            value: dataForm.period5!
          },
          {
            period: EnumPeriodo.Periodo6,
            value: dataForm.period6!
          }
        ]
      })
  }

  const hasAtrValues = Object.values(atr).some(value => value !== undefined && value !== null)

  const request: ChangeContractOwnershipModel = {
    clientId: clientID,
    codContractOld: activeContract.contractCode,
    contractNew: contract,
    atr: hasAtrValues ? atr : null,
    cnae: dataForm.cnae,
    oldContractState:
      dataForm.changeType === CHANGE_TYPE.CHANGE_OWNER_ONLY
        ? EnumEstadoCliente.Continuado
        : undefined
  }
  return await apiChangeOwner(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const revertChangeOwner = async (
  newContractId: number,
  oldContractId: number
): Promise<ServiceResponse<any>> => {
  const request: RevertChangeOwnershipModel = {
    newContractId,
    oldContractId
  }
  return await apiRevertChangeOwner(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const terminateContract = async (
  contractID: number,
  terminationDate: Date
): Promise<ServiceResponse<any>> => {
  const request: TerminateContractOwnershipModel = {
    contractId: contractID,
    terminationDate: moment(terminationDate).format('YYYY-MM-DD')
  }
  return await apiTerminateContract(request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchSettlementGroupsByContract = async (contract: ContractModel): Promise<any> => {
  return await apiFetchSettlementGroupsByContractID({
    contractID: contract.id,
    startDate: contract.startDate,
    endDate: contract.endDate
  })
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createQuantitiesForContract = async (
  contractID: number,
  contractQuantity: NewContractQuantity
): Promise<ServiceResponse<ContractQuantity | undefined>> => {
  return await apiCreateContractQuantityForContract(contractID, contractQuantity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateQuantitiesForContract = async (
  contractID: number,
  contractQuantity: ContractQuantityRequest
): Promise<ServiceResponse<ContractQuantity | undefined>> => {
  return await apiUpdateContractQuantityForContract(contractID, contractQuantity)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getClientAccounts = async (
  subjectID: number
): Promise<ServiceResponse<Account[] | undefined>> => {
  return await apiGetAccountsForSubject(subjectID)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const fetchAnnexedContracts = async (
  commodity?: number,
  country?: number
): Promise<ServiceResponse<AnnexedParamsModel[] | undefined>> => {
  return await apiGetAnnexedContracts(commodity, country)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createAnnexedContract = async (
  request: AnnexedContractModelPostRequest[],
  contractId: number
): Promise<ServiceResponse<AnnexedParamsModel[] | undefined>> => {
  return await apiPostAnnexedContract(request, contractId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateAnnexedContract = async (
  contractId: number,
  annexedContractId: number,
  request: AnnexedContractModelPatchRequest
): Promise<ServiceResponse<AnnexedParamsModel | undefined>> => {
  return await apiPutAnnexedContract(contractId, annexedContractId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const deleteAnnexedContract = async (
  contractId: number,
  annexedContractId: number
): Promise<ServiceResponse<any>> => {
  return await apiDeleteAnnexedContract(contractId, annexedContractId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const getContractOrders = async (
  contractId
): Promise<ServiceResponse<ContractOrdersModel[] | undefined>> => {
  return await apiGetContractOrders(contractId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const createContractOrders = async (
  contractId: number,
  request: ContractOrdersRequestModel
): Promise<ServiceResponse<ContractOrdersModel | undefined>> => {
  return await apiPostContractOrders(contractId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const updateContractOrders = async (
  contractId: number,
  contractOrderId: number,
  request: ContractOrdersRequestModel
): Promise<ServiceResponse<ContractOrdersModel | undefined>> => {
  return await apiPatchContractOrders(contractId, contractOrderId, request)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const DeleteContractOrders = async (
  contractId: number,
  contractOrderId: number
): Promise<ServiceResponse<ContractOrdersModel | undefined>> => {
  return await apiDeleteContractOrders(contractId, contractOrderId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

export const checkContractSignDate = async (
  procedureId: number
): Promise<ServiceResponse<boolean | undefined>> => {
  return await apiCheckContractSignDateByProcedureId(procedureId)
    .then(response => {
      return createSuccessServiceResponse(response)
    })
    .catch(error => {
      return createErrorServiceResponse(error)
    })
}

// -------- Contracts DataTable Action Icons Configuration --------

export const getGlobalActionsConfiguration = (): Array<DataTableActionConfiguration> => {
  return [getContractTerminatedAction(), getContractObjectionsAction()]
}

const getContractTerminatedAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.ERROR,
    tooltip: i18n.t('clientFile:components.contractsCard.dataTable.actions.contractTerminated')
  }
}

const getContractObjectionsAction = (): DataTableActionConfiguration => {
  return {
    icon: ActionIcons.WARNING,
    tooltip: 'Objecciones'
  }
}

export const completeDataTableActions = (items: ContractModel[]): ContractModel[] => {
  return items.map(item => completeDataTableItemActions(item))
}

export const completeDataTableItemActions = (item: any): any => {
  const globalActions = getGlobalActionsConfiguration()
  const applicableActions = filterApplicableActionsConfiguration(item, globalActions)

  return {
    ...item,
    actions:
      globalActions && globalActions.length > 0 && applicableActions.length > 0
        ? generateIconsFromConfiguration(
            item,
            applicableActions,
            new Map<ActionIcons, Function>([])
          )
        : undefined
  }
}

export const filterApplicableActionsConfiguration = (
  item: any,
  configurations: Array<DataTableActionConfiguration>
): Array<DataTableActionConfiguration> => {
  let applicableActionsConfiguration: Array<DataTableActionConfiguration> = []
  configurations.forEach(configuration => {
    switch (configuration.icon) {
      case ActionIcons.ERROR: {
        if (
          enumEnumToValueNumber(EnumEstadoCliente, item.status) === EnumEstadoCliente.Baja &&
          item.objecionList &&
          item.objecionList.length > 0
        ) {
          applicableActionsConfiguration.push({
            ...configuration,
            tooltip: item.objecionList[0]?.comments || configuration.tooltip
          })
        }
        break
      }
    }
  })
  return applicableActionsConfiguration
}
